/**
 * Top bar. Every control is `shrink-0 whitespace-nowrap`, so a label never
 * breaks onto a second line. The header is a CSS container, and optional
 * pieces drop out by the header's own width (which depends on the rail and
 * side panel, not just the viewport): brush ≥ 1360px, nudge/zoom + theme
 * label + wide search ≥ 1200px, range text ≥ 1000px, compact job/search
 * below that. At 1024×768 with the rail open (header 804px) it is still one
 * 40px row; if a header is squeezed below ~780px (side panel open on a small
 * screen) whole controls wrap onto a second row that stays in flow, so the
 * warnings bar always sits below the header and is never overlapped.
 * Narrow viewports (< 900px) get the phone layout, one 44px row
 * (NarrowTopBar below).
 */
import { useEffect, useRef, useState } from "react";
import { BucketPicker } from "@/components/BucketPicker";
import { JobSwitcher } from "@/components/JobSwitcher";
import { LiveToggle } from "@/components/LiveToggle";
import { LIVE_DOT } from "@/components/liveDot";
import { MiniBrush } from "@/components/MiniBrush";
import { SearchBox } from "@/components/SearchBox";
import { ThemeToggle } from "@/components/ThemeToggle";
import { TimeRangePicker } from "@/components/TimeRangePicker";
import { useSseStore } from "@/api/sse";
import { useShortcuts } from "@/lib/shortcuts";
import { useUi } from "@/state/ui";
import { useUrlState } from "@/state/useUrlState";

export function TopBar() {
  const narrow = useUi((s) => s.narrow);
  if (narrow) return <NarrowTopBar />;
  return (
    <header className="@container relative flex min-h-[var(--topbar-h)] shrink-0 flex-wrap items-center gap-x-1 gap-y-1.5 border-b border-border bg-bg-elev px-2 py-1.5 whitespace-nowrap">
      <JobSwitcher />
      <TimeRangePicker brush={<MiniBrush />} />
      <BucketPicker />
      <LiveToggle />
      <div className="min-w-1 flex-1" />
      <SearchBox />
      <Actions />
    </header>
  );
}

/** Which panel hangs below the phone top bar's row, if any. */
type Sheet = "range" | "more";

/**
 * The phone top bar: one 44px row (#129). Menu, the job, the range as one
 * chip, search, ⋯. The chip opens the range controls (presets, now/fixed,
 * bucket, live) in a panel below the row; ⋯ opens jump / theme / shortcuts /
 * side panel. Search replaces the row's middle while it is open ("/" opens
 * it too). A panel closes on Escape, a tap outside the bar, or its button.
 */
export function NarrowTopBar() {
  const railOpen = useUi((s) => s.railOpen);
  const toggleRail = useUi((s) => s.toggleRail);
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [searching, setSearching] = useState(false);
  const header = useRef<HTMLElement>(null);
  const toggle = (s: Sheet) => setSheet((cur) => (cur === s ? null : s));
  useShortcuts(
    [
      {
        key: "/",
        description: "focus search",
        group: "navigation",
        handler: () => {
          setSheet(null);
          setSearching(true);
        },
      },
    ],
    !searching,
  );
  useEffect(() => {
    if (!sheet) return;
    const onDown = (e: PointerEvent) => {
      if (!header.current?.contains(e.target as Node)) setSheet(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSheet(null);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [sheet]);
  return (
    <header
      ref={header}
      className="relative flex h-11 shrink-0 items-center gap-1.5 border-b border-border bg-bg-elev px-2 whitespace-nowrap"
    >
      <button
        type="button"
        className={`ctl topbar-ctl shrink-0 ${railOpen ? "on" : ""}`}
        aria-pressed={railOpen}
        aria-label="menu"
        title="menu"
        onClick={toggleRail}
      >
        ☰
      </button>
      {searching ? (
        <>
          <SearchBox compact onDone={() => setSearching(false)} />
          <button
            type="button"
            className="ctl topbar-ctl shrink-0"
            aria-label="close search"
            title="close search"
            onClick={() => setSearching(false)}
          >
            ×
          </button>
        </>
      ) : (
        <>
          <JobSwitcher fill />
          <RangeChip open={sheet === "range"} onToggle={() => toggle("range")} />
          <button
            type="button"
            className="ctl topbar-ctl shrink-0"
            aria-label="search"
            title="search  /"
            onClick={() => {
              setSheet(null);
              setSearching(true);
            }}
          >
            ⌕
          </button>
          <button
            type="button"
            className={`ctl topbar-ctl shrink-0 ${sheet === "more" ? "on" : ""}`}
            aria-label="more"
            title="more"
            aria-expanded={sheet === "more"}
            aria-controls="topbar-more"
            onClick={() => toggle("more")}
          >
            ⋯
          </button>
        </>
      )}
      {sheet === "range" && (
        <div
          id="topbar-range"
          role="group"
          aria-label="time range"
          className="absolute inset-x-0 top-full z-30 flex flex-wrap items-center gap-1.5 border-b border-border bg-bg-elev p-2 shadow-lg"
        >
          <TimeRangePicker />
          <BucketPicker />
          <LiveToggle />
        </div>
      )}
      {sheet === "more" && (
        <div
          id="topbar-more"
          className="absolute inset-x-0 top-full z-30 border-b border-border bg-bg-elev p-2 shadow-lg"
          // a pick closes the panel
          onClick={(e) => {
            if ((e.target as HTMLElement).closest("button")) setSheet(null);
          }}
        >
          <Actions labelled />
        </div>
      )}
    </header>
  );
}

/** The range as one chip: the preset (or "custom"), a ▾, and the live dot. */
function RangeChip({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const { state } = useUrlState();
  const status = useSseStore((s) => s.status);
  const label = state.range.from ? "custom" : (state.range.preset ?? "range");
  return (
    <button
      type="button"
      className={`ctl topbar-ctl flex shrink-0 items-center gap-1 ${open ? "on" : ""}`}
      aria-expanded={open}
      aria-controls="topbar-range"
      title={`time range: ${label}${state.live ? `, live (${status})` : ", live off"}`}
      onClick={onToggle}
    >
      {state.live && <span className={`inline-block h-2 w-2 rounded-full ${LIVE_DOT[status]}`} aria-hidden />}
      {label} ▾
    </button>
  );
}

/** `labelled`: the phone top bar's ⋯ panel, where each button says what it does. */
function Actions({ labelled = false }: { labelled?: boolean }) {
  const toggleSide = useUi((s) => s.toggleSidePanel);
  const sideOpen = useUi((s) => s.sidePanelOpen);
  const setShortcutsOpen = useUi((s) => s.setShortcutsOpen);
  const setPaletteOpen = useUi((s) => s.setPaletteOpen);
  const ctl = labelled ? "ctl topbar-ctl shrink-0" : "ctl shrink-0";
  return (
    <div className={labelled ? "flex flex-wrap items-center gap-1.5" : "flex shrink-0 items-center gap-1.5"}>
      <button
        type="button"
        className={ctl}
        title="jump to a page or command (Ctrl K / ⌘K)"
        onClick={() => setPaletteOpen(true)}
        aria-label="jump to"
      >
        {labelled ? "⌘K jump to" : "⌘K"}
      </button>
      <ThemeToggle labelled={labelled} />
      <button
        type="button"
        className={ctl}
        title="keyboard shortcuts (?)"
        onClick={() => setShortcutsOpen(true)}
        aria-label="keyboard shortcuts"
      >
        {labelled ? "? shortcuts" : "?"}
      </button>
      <button
        type="button"
        className={`${ctl} ${sideOpen ? "on" : ""}`}
        aria-pressed={sideOpen}
        title="toggle side panel"
        onClick={toggleSide}
        aria-label="side panel"
      >
        {labelled ? "▥ side panel" : "▥"}
      </button>
    </div>
  );
}
